// ABOUTME: Coordinates access token refreshes across every browser tab of the origin.
// ABOUTME: Serialises refreshes with a web lock and broadcasts the rotated pair to sibling tabs.
import { updateToken, logout, UserState } from './user/authentication/authSlice';

// Web Lock held for the duration of a refresh. Scoped to the origin, so it is
// shared by every tab — unlike authMutex, which only serialises one tab.
const AUTH_LOCK_NAME = 'stockhub-auth-refresh';

// Channel the tab that refreshed uses to hand the new pair to its siblings.
const AUTH_CHANNEL_NAME = 'stockhub-auth';
const TOKENS_MESSAGE = 'tokens';

let channel;

// Browsers without BroadcastChannel keep working; they just fall back to the
// per-tab behaviour, so every read is guarded against a missing channel.
const getChannel = () => {
    if (channel === undefined) {
        channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel(AUTH_CHANNEL_NAME) : null;
    }
    return channel;
};

// Web Locks need a secure context; where they are unavailable the callback runs
// directly and refreshes are serialised by authMutex within this tab alone.
const withCrossTabLock = async (callback) => {
    if (typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function') {
        // `entered` separates the lock manager refusing us (an opaque origin, a
        // sandboxed frame) from the callback failing on its own. Only the former
        // falls back to running unserialised; the latter must propagate, or the
        // refresh would run a second time.
        let entered = false;
        try {
            return await navigator.locks.request(AUTH_LOCK_NAME, () => {
                entered = true;
                return callback();
            });
        } catch (error) {
            if (entered) throw error;
        }
    }
    return callback();
};

// A refresh that never answers would sit on the cross-tab lock for as long as
// the browser takes to abandon the connection — minutes, on a black-holed
// socket. Give up sooner and report it as the timeout it is.
const REFRESH_TIMEOUT_MS = 15000;
const TIMED_OUT = Symbol('refresh-timed-out');

const callRefresh = async (refresh, refreshToken, timeoutMs) => {
    const pending = refresh(refreshToken);

    let timer;
    const expiry = new Promise((resolve) => {
        timer = setTimeout(() => resolve(TIMED_OUT), timeoutMs);
    });

    const outcome = await Promise.race([pending, expiry]);
    clearTimeout(timer);

    if (outcome !== TIMED_OUT) return outcome;

    // Abort so a late reply cannot half-update the store: the refreshToken
    // matcher in authSlice sets the access token but leaves the rotated refresh
    // token behind, which would strand the session at its next expiry.
    pending?.abort?.();
    return { error: { status: 'TIMEOUT_ERROR' } };
};

// A 4xx from /users/refresh means the server looked at our refresh token and
// refused it — the session really is gone. Anything else (a 5xx, or RTK Query's
// non-numeric FETCH_ERROR / TIMEOUT_ERROR / PARSING_ERROR) is the network or the
// server faulting, and must not cost the user their session.
const isCredentialRefused = (error) =>
    typeof error?.status === 'number' && error.status >= 400 && error.status < 500;

// Stamped with the sender's user id: localStorage is shared per origin, so two
// tabs of the same browser profile can be signed in as different accounts, and a
// tab must never adopt a pair minted for somebody else.
const publishTokens = (userId, { token, refreshToken, expires_at }) => {
    getChannel()?.postMessage({ type: TOKENS_MESSAGE, userId, tokens: { token, refreshToken, expires_at } });
};

/**
 * Refresh the access token and replay the request that got the 401.
 *
 * `staleToken` is the access token the failed request was sent with — captured
 * before it goes out, not after its 401 comes back, so a refresh that landed
 * while the response was in flight is still seen as a change.
 * Refresh tokens are single-use and rotate server side, so the moment
 * another request — or another tab — has refreshed, ours is dead and spending
 * it would answer 404 and log the user out. Comparing against `staleToken`
 * once we hold the lock tells us that happened, and we simply retry instead.
 *
 * `refresh` receives the current refresh token and resolves to the RTK Query
 * result of the refresh call; `retry` replays the original request; and
 * `failedResult` is the 401 we return unchanged when the session is really gone.
 */
export const refreshTokenAndRetry = async ({ api, staleToken, refresh, retry, failedResult, timeoutMs = REFRESH_TIMEOUT_MS }) => {
    // Only the decision is taken under the lock. The replay runs after it is
    // released, so a sibling tab waiting to refresh is not also made to wait out
    // our round trip.
    const replay = await withCrossTabLock(async () => {
        // Yield once so a token broadcast queued by the tab that just released
        // the lock is applied before we decide whether a refresh is needed.
        await new Promise((resolve) => setTimeout(resolve, 0));

        if (api.getState().auth.token !== staleToken) {
            return true;
        }

        const response = await callRefresh(refresh, api.getState().auth.refreshToken, timeoutMs);

        // The token is replaced only on success. Clearing it up front would
        // persist a "logged in without tokens" state that any tab opening
        // mid-refresh would rehydrate, and would make a transient failure
        // unrecoverable.
        if (response?.data) {
            api.dispatch(updateToken(response.data));
            publishTokens(api.getState().auth.user?.id, response.data);
            return true;
        }

        // The refresh is a round trip, so a sibling tab may have finished its own
        // and broadcast the new pair while ours was on the wire — which is exactly
        // why ours came back refused. Adopting it beats tearing the session down.
        if (api.getState().auth.token !== staleToken) {
            return true;
        }

        if (isCredentialRefused(response?.error)) {
            api.dispatch(logout());
        }
        return false;
    });

    return replay ? retry() : failedResult;
};

/**
 * Adopt tokens refreshed by a sibling tab. Returns an unsubscribe function.
 *
 * Logout is deliberately not broadcast: a tab that logs itself out must not be
 * able to take every other tab down with it.
 */
export const subscribeToAuthSync = (store) => {
    const authChannel = getChannel();
    if (!authChannel) return () => {};

    const onMessage = (event) => {
        const message = event?.data;
        if (message?.type !== TOKENS_MESSAGE) return;

        const { auth } = store.getState();
        // A tab that has logged out must not be resurrected by a sibling.
        if (auth.status !== UserState.LOGGED_IN) return;
        // Only adopt a pair minted for the account this tab is signed in as. An
        // absent id on either side is treated as a mismatch rather than a match.
        if (message.userId == null || message.userId !== auth.user?.id) return;

        store.dispatch(updateToken(message.tokens));
    };

    authChannel.addEventListener('message', onMessage);
    return () => authChannel.removeEventListener('message', onMessage);
};
